import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { errorMessages } from '../../../utils/methods';
import { getMemberSelection, getLockers, deleteLocker, getLockerArea } from '../../../api/ticket';
import LockerUpdateModal from './modal/LockerUpdateModal/LockerUpdateModal';
import EnterSaleInfoModal from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModal';
import LockerDeleteModal from './modal/LockerDeleteModal/LockerDeleteModal';
import LockerSpotSettingModal from './modal/LockerSpotSettingModal/LockerSpotSettingModal';
import SettingsIcon from '@mui/icons-material/Settings';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { GlobalStyles, Tooltip } from '@mui/material';
import {
  PageContainer,
  ControlSection,
  ControlLeftSection,
  LockerGrid,
  LockerTypeSelect,
  AreaManageButton,
  PrintButton,
  SortToggleButton,
  PrintOnlyHeader,
  PrintTitle,
  PrintSubtitle,
  LockerMenuItem,
  LockerPage,
  SearchInputWrapper,
  SearchInput,
  SearchIcon,
} from './LockerManagementStyled';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import LockerCard from './LockerCard';
import useUserAuth from '../../../utils/useUserAuth';

// 출력용 Portal 컨테이너 ID (body 직속으로 붙음)
const PRINT_PORTAL_ID = 'locker-print-portal';

// 한 페이지당 락커 개수 (8열 × 9행 = 72)
const LOCKERS_PER_PAGE = 72;

// LockerGrid 의 cell width / gap (LockerManagementStyled.LockerGrid 와 일치해야 함)
const LOCKER_CELL_WIDTH = 140;
const LOCKER_GRID_GAP = 16;

// 출력 전용 전역 스타일
// - 화면에서는 Portal 숨김, 앱 UI만 노출
// - 출력 시엔 앱 UI 전부 display:none 처리하고 Portal만 노출
//   → position:absolute/visibility 트릭 없이 정상 문서 흐름에서 렌더링되므로
//      페이지 분리(page-break) 가 환경 무관하게 일관 동작
// - html/body width를 100%로 고정해서 지점별 콘텐츠 너비 차이 제거
const PRINT_STYLES = {
  [`#${PRINT_PORTAL_ID}`]: {
    display: 'none', // 화면에서는 포탈 숨김
  },
  '@media print': {
    'html, body': {
      margin: '0 !important',
      padding: '0 !important',
      width: '100% !important',
      height: 'auto !important',
    },
    [`body > *:not(#${PRINT_PORTAL_ID})`]: {
      display: 'none !important',
    },
    [`#${PRINT_PORTAL_ID}`]: {
      display: 'block !important',
      width: '100% !important', // 페이지 전체 너비 고정
    },
    '*': {
      WebkitPrintColorAdjust: 'exact !important',
      printColorAdjust: 'exact !important',
    },
    // A4 고정 + 여백 0.5cm
    '@page': { size: 'A4 portrait', margin: '0.5cm' },
  },
};

const getTodayString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Main Component
const LockerManagement = ({ branchId: propBranchId, centerId: propCenterId }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const canLockerEdit = useUserAuth('CENTER_LOCKER_VIEW_EDIT');

  // props로 받은 값이 없으면 Redux에서 가져옴
  const branchId = propBranchId || user?.branch?.branchId;
  const centerId = propCenterId || user?.headBranch?.headBranchId;
  const toast = useToast();
  const queryClient = useQueryClient();

  // State - Modal & UI
  const [open, setOpen] = useState(false);
  // 수정 모달의 '+ 연장 추가' → 판매등록(라커 탭)을 그 자리·기간 프리셋으로 연다
  const [extendSale, setExtendSale] = useState(null); // {userId, preset} | null
  const [openDelete, setOpenDelete] = useState(false);
  const [lockerDetailSingle, setLockerDetailSingle] = useState({});
  const [lockerType, setLockerType] = useState('');
  const [openLockerAreaModal, setOpenLockerAreaModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [printPortalEl, setPrintPortalEl] = useState(null);
  // 'horizontal': 1,2,3 → 다음줄 (기본, auto-fill row-major)
  // 'vertical':   위→아래 → 다음 열 (column-major, grid-auto-flow:column)
  const [sortDirection, setSortDirection] = useState('horizontal');
  // 실제 화면에 들어가는 열 수 (auto-fill 결과를 ResizeObserver 로 측정)
  // 세로 정렬에서 column-major 그리드 구성에 필요
  const [gridColumns, setGridColumns] = useState(1);
  const gridRef = useRef(null);

  // 외부에서 라커 번호로 진입(예: 회원 상세의 라커 카드 클릭)했을 때 해당 라커로 이동/강조
  const [searchParams] = useSearchParams();
  const targetLockerNumber = searchParams.get('lockerNumber');
  const targetLockerAreaName = searchParams.get('lockerAreaName');
  const [highlightedLockerId, setHighlightedLockerId] = useState(null);

  // 출력용 Portal 컨테이너를 body 직속에 마련
  // (body 직속이어야 print CSS에서 'body > *:not(#portal)' 로 제어 가능)
  useEffect(() => {
    let el = document.getElementById(PRINT_PORTAL_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = PRINT_PORTAL_ID;
      document.body.appendChild(el);
    }
    setPrintPortalEl(el);
  }, []);

  // React Query - Data Fetching
  // 회원 목록 조회
  const { data: memberList = [] } = useQuery({
    queryKey: ['members', branchId],
    queryFn: async () => {
      const result = await getMemberSelection(branchId, user.token);
      return result.map(member => ({
        label: member.memberName,
        value: member.memberId,
      }));
    },
    enabled: !!branchId && !!user?.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 락커 목록 조회
  const { data: lockerList = [] } = useQuery({
    queryKey: ['lockers', branchId],
    queryFn: () => getLockers(branchId, user.token),
    enabled: !!branchId && !!user?.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 락커 구역 목록 조회
  const { data: lockerAreas = [] } = useQuery({
    queryKey: ['lockerAreas', branchId],
    queryFn: () => getLockerArea(branchId, user.token),
    enabled: !!branchId && !!user?.token,
    onError: error => {
      console.log('getLockerAreaDatas 오류 : ', error);
    },
  });

  // React Query - Mutations
  // 락커 삭제
  const deleteMutation = useMutation({
    mutationFn: lockerId => deleteLocker(lockerId, user.token),
    onSuccess: () => {
      toast.success(t('The locker has been deleted'));
      queryClient.invalidateQueries({ queryKey: ['lockers', branchId] });
    },
    onError: () => {
      toast.error('삭제할 라커가 없습니다');
    },
  });

  const deleteLockerSingle = () => {
    if (!lockerList || lockerList.length === 0) {
      toast.error('삭제할 라커가 없습니다');
      return;
    }
    const lockerWithMaxId = lockerList.reduce((maxLocker, currentLocker) => {
      return currentLocker.lockerId > maxLocker.lockerId ? currentLocker : maxLocker;
    }, lockerList[0]);
    deleteMutation.mutate(lockerWithMaxId.lockerId);
  };

  // Computed Values
  // 선택된 락커 구역 및 검색어에 따라 락커 필터링
  const filteredLockers = useMemo(() => {
    let result = lockerList || [];

    // 락커 구역 필터링
    if (lockerType && result.length > 0) {
      result = result.filter(locker => locker.lockerAreaId === Number(lockerType));
    }

    // 검색어 필터링 (회원명 또는 전화번호)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(locker => {
        const memberName = (locker.memberName || locker.unregisteredMemberName || '').toLowerCase();
        const phoneNumber = (locker.memberPhone || locker.phoneNumber || '').replace(/-/g, '');
        const queryWithoutDash = query.replace(/-/g, '');

        return memberName.includes(query) || phoneNumber.includes(queryWithoutDash);
      });
    }

    return result;
  }, [lockerType, lockerList, searchQuery]);

  // 출력 시 72개씩 페이지 분할 (Portal에서 사용)
  const lockerPages = useMemo(() => {
    if (filteredLockers.length === 0) return [];
    const pages = [];
    for (let i = 0; i < filteredLockers.length; i += LOCKERS_PER_PAGE) {
      pages.push(filteredLockers.slice(i, i + LOCKERS_PER_PAGE));
    }
    return pages;
  }, [filteredLockers]);

  // 세로 정렬에서 column-major 그리드를 그리기 위한 row 수
  // 마지막 열에 빈 칸이 생기더라도 격자 정렬은 유지됨
  const gridRows = useMemo(() => {
    if (filteredLockers.length === 0 || gridColumns < 1) return 0;
    return Math.ceil(filteredLockers.length / gridColumns);
  }, [filteredLockers.length, gridColumns]);

  // Effects
  // 락커 구역 로드 시 또는 삭제 시 선택 처리
  useEffect(() => {
    if (lockerAreas?.length > 0) {
      const isCurrentTypeExists = lockerAreas.some(area => area.id === lockerType);
      if (!lockerType || !isCurrentTypeExists) {
        setLockerType(lockerAreas[0].id);
      }
    } else {
      setLockerType('');
    }
  }, [lockerAreas]);

  // 라커 번호 딥링크 처리: 해당 라커가 배정된 구역으로 전환 → 스크롤 → 강조(클릭 전까지 유지)
  // 같은 번호+구역으로는 한 번만 적용 (라커 목록 refetch 시 재강조 방지)
  const appliedLockerKeyRef = useRef(null);
  useEffect(() => {
    if (!targetLockerNumber || !lockerList?.length || !lockerAreas?.length) return;

    const appliedKey = `${targetLockerNumber}|${targetLockerAreaName || ''}`;
    if (appliedLockerKeyRef.current === appliedKey) return;

    // 라커 번호는 구역별로 중복될 수 있어, 구역명(lockerAreaName)이 있으면 함께 매칭한다.
    const target = lockerList.find(
      locker =>
        String(locker.lockerNumber) === String(targetLockerNumber) &&
        (!targetLockerAreaName || locker.lockerAreaName === targetLockerAreaName),
    );
    if (!target) return;

    appliedLockerKeyRef.current = appliedKey;

    // 라커가 속한 구역을 select 가 쓰는 area.id 값으로 정확히 맞춰 전환
    // (그래야 '구역 기본선택' effect 가 첫 구역으로 덮어쓰지 않음)
    const targetArea = lockerAreas.find(area => String(area.id) === String(target.lockerAreaId));
    setLockerType(targetArea ? targetArea.id : target.lockerAreaId);
    setHighlightedLockerId(target.lockerId);

    // 구역 전환 후 렌더가 끝난 다음 스크롤
    const scrollTimer = setTimeout(() => {
      document
        .getElementById(`locker-card-${target.lockerId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);

    return () => clearTimeout(scrollTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLockerNumber, targetLockerAreaName, lockerList, lockerAreas]);

  // 화면용 LockerGrid 의 실제 열 수를 측정해서 세로 정렬용 그리드 계산에 사용
  // auto-fill 공식: floor((width + gap) / (cellWidth + gap))
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const updateColumns = () => {
      const width = node.offsetWidth;
      const cols = Math.max(1, Math.floor((width + LOCKER_GRID_GAP) / (LOCKER_CELL_WIDTH + LOCKER_GRID_GAP)));
      setGridColumns(cols);
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Handlers
  const handleRefreshData = () => {
    queryClient.invalidateQueries({ queryKey: ['lockerAreas', branchId] });
    queryClient.invalidateQueries({ queryKey: ['lockers', branchId] });
  };

  const handlePrint = () => window.print();

  const handleToggleSortDirection = () => {
    setSortDirection(prev => (prev === 'horizontal' ? 'vertical' : 'horizontal'));
  };

  // 프린트 헤더에 표시할 현재 선택된 구역 이름
  const selectedAreaName = lockerAreas?.find(area => area.id === lockerType)?.lockerAreaName || '전체';
  const branchName = user?.branch?.branchName || '';

  // Render
  if (!branchId) {
    return (
      <PageContainer>
        <div style={{ padding: '30px 0', fontSize: '20px', fontWeight: 500 }}>지점을 선택해주세요</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GlobalStyles styles={PRINT_STYLES} />

      {/* 상단 컨트롤 영역 */}
      <ControlSection>
        <ControlLeftSection>
          <LockerTypeSelect value={lockerType} onChange={e => setLockerType(e.target.value)} size="small">
            {lockerAreas?.map((locker, index) => (
              <LockerMenuItem key={locker.id} value={locker.id}>
                {locker.lockerAreaName}
              </LockerMenuItem>
            ))}
          </LockerTypeSelect>
          <Tooltip title={!canLockerEdit ? '권한이 없습니다' : ''} arrow>
            <AreaManageButton
              variant="contained"
              startIcon={<SettingsIcon style={{ fontSize: '18px' }} />}
              onClick={canLockerEdit ? () => setOpenLockerAreaModal(true) : undefined}
              style={!canLockerEdit ? { opacity: 0.5, cursor: 'default' } : {}}>
              {t('라커 구역 관리')}
            </AreaManageButton>
          </Tooltip>
          <SortToggleButton
            variant="outlined"
            startIcon={
              sortDirection === 'horizontal' ? (
                <SwapHorizIcon style={{ fontSize: '18px' }} />
              ) : (
                <SwapVertIcon style={{ fontSize: '18px' }} />
              )
            }
            onClick={handleToggleSortDirection}>
            {sortDirection === 'horizontal' ? t('가로 정렬') : t('세로 정렬')}
          </SortToggleButton>
          <PrintButton
            variant="outlined"
            startIcon={<PrintOutlinedIcon style={{ fontSize: '18px' }} />}
            onClick={handlePrint}>
            {t('출력')}
          </PrintButton>
        </ControlLeftSection>
        <SearchInputWrapper>
          <SearchInput
            type="text"
            placeholder={t('이름 또는 연락처로 검색')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <SearchIcon>
            <SearchOutlinedIcon style={{ fontSize: '20px' }} />
          </SearchIcon>
        </SearchInputWrapper>
      </ControlSection>

      {/* 화면용 락커 그리드 (출력 시에는 display:none) */}
      <LockerGrid
        ref={gridRef}
        sortDirection={sortDirection}
        columns={gridColumns}
        rows={gridRows}>
        {filteredLockers?.map((locker, index) => (
          <LockerCard
            key={locker.lockerId || index}
            elementId={`locker-card-${locker.lockerId}`}
            highlighted={highlightedLockerId === locker.lockerId}
            locker={locker}
            index={index}
            onLockerClick={lockerDetail => {
              if (!canLockerEdit) return;
              setHighlightedLockerId(null);
              setLockerDetailSingle(lockerDetail);
              setOpen(true);
            }}
          />
        ))}
      </LockerGrid>

      {/* 출력 전용 Portal: body 직속으로 렌더링되어 페이지 브레이크 정상 동작 */}
      {printPortalEl &&
        createPortal(
          <>
            <PrintOnlyHeader>
              <PrintTitle>락커 현황 - {selectedAreaName}</PrintTitle>
              <PrintSubtitle>
                {branchName} · 출력일 {getTodayString()} · 총 {filteredLockers.length}개
              </PrintSubtitle>
            </PrintOnlyHeader>
            {lockerPages.map((pageLockers, pageIdx) => (
              <LockerPage key={pageIdx} isLastPage={pageIdx === lockerPages.length - 1}>
                <LockerGrid>
                  {pageLockers.map((locker, idx) => (
                    <LockerCard
                      key={locker.lockerId || `${pageIdx}-${idx}`}
                      locker={locker}
                      index={pageIdx * LOCKERS_PER_PAGE + idx}
                      onLockerClick={() => {}}
                    />
                  ))}
                </LockerGrid>
              </LockerPage>
            ))}
          </>,
          printPortalEl,
        )}

      {/* 모달들 */}
      <LockerUpdateModal
        memberList={memberList}
        open={open}
        setOpen={setOpen}
        lockerDetailSingle={lockerDetailSingle}
        setLockerDetailSingle={setLockerDetailSingle}
        branchId={branchId}
        branchIdData={branchId}
        centerIdData={centerId}
        onExtend={detail => {
          setOpen(false);
          setExtendSale({
            userId: detail.memberId,
            preset: {
              lockerId: detail.lockerId,
              lockerNumber: detail.lockerNumber,
              lockerAreaName: detail.lockerAreaName,
              expireDate: detail.expireDate,
            },
          });
        }}
      />
      {/* '+ 연장 추가' 판매등록 — 회원상세와 동일한 연장 프리셋 사용 */}
      {extendSale && (
        <EnterSaleInfoModal
          userInfo={{ userId: extendSale.userId, userType: 'member' }}
          open={Boolean(extendSale)}
          onOpen={() => {}}
          onClose={() => setExtendSale(null)}
          hideButton={true}
          initialLockerExtension={extendSale.preset}
          fetchData={() => {
            queryClient.invalidateQueries({ queryKey: ['lockers', branchId] });
            queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
          }}
        />
      )}
      <LockerDeleteModal open={openDelete} setOpen={setOpenDelete} handleDelete={deleteLockerSingle} />
      <LockerSpotSettingModal
        branchIdData={branchId}
        open={openLockerAreaModal}
        handleClose={() => setOpenLockerAreaModal(false)}
        lockerAreas={lockerAreas}
        branchId={branchId}
        onSuccess={handleRefreshData}
      />
    </PageContainer>
  );
};

export default LockerManagement;
